import { useState, useRef, useEffect } from 'react';
import SaveButton from './SaveButton';
import ShareModal, { createShareableLesson } from './ShareModal';
import CreateCommunityModal from './CreateCommunityModal';
import ReactionButtons from './ReactionButtons';
import QuizModal from './QuizModal';
import TutorChat from './TutorChat';
import { buildTextSaveItem } from '../lib/savedContentService';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';

const MODEL_META = {
  anthropic:  { label: 'Claude',      color: '#7C3AED', emoji: '🟣' },
  openai:     { label: 'GPT-4o',      color: '#10A37F', emoji: '🟢' },
  gemini:     { label: 'Gemini',      color: '#4285F4', emoji: '🔵' },
  perplexity: { label: 'Perplexity',  color: '#20808D', emoji: '🩵' },
};

// Build a plain-text version of the lesson for TTS
function buildSpeechText(lesson) {
  const parts = [];
  if (lesson.title)   parts.push(lesson.title + '.');
  if (lesson.summary) parts.push(lesson.summary);
  if (lesson.keyPoints?.length) {
    parts.push('Key points.');
    lesson.keyPoints.forEach(p => parts.push(p + '.'));
  }
  if (lesson.content?.length) {
    lesson.content.forEach(s => {
      if (s.heading) parts.push(s.heading + '.');
      if (s.body)    parts.push(s.body);
    });
  }
  if (lesson.practicalExample) {
    parts.push('Practical example.');
    parts.push(lesson.practicalExample);
  }
  if (lesson.takeaways?.length) {
    parts.push('Key takeaways.');
    lesson.takeaways.forEach(t => parts.push(t + '.'));
  }
  if (lesson.nextSteps) {
    parts.push('Next steps.');
    parts.push(lesson.nextSteps);
  }
  return parts.join(' ');
}

function TextContent({ lesson, subject, onRetryModel, onNextLesson, searchParams, autoPlay }) {
  const { user } = useAuth();
  const [showShare,      setShowShare]      = useState(false);
  const [shareUrl,       setShareUrl]       = useState(null);
  const [creatingLink,   setCreatingLink]   = useState(false);

  const [showCreateComm, setShowCreateComm] = useState(false);
  const [showQuiz,       setShowQuiz]       = useState(false);
  const [showTutor,      setShowTutor]      = useState(false);

  // ── Read Aloud ─────────────────────────────────────────────────────────────
  const [speaking,   setSpeaking]   = useState(false);
  const [ttsSupport, setTtsSupport] = useState(false);
  const utterRef = useRef(null);

  useEffect(() => {
    setTtsSupport('speechSynthesis' in window);
    // Stop speech if lesson changes
    return () => { window.speechSynthesis?.cancel(); setSpeaking(false); };
  }, [lesson]);

  // Auto-play TTS if audio format
  useEffect(() => {
    if (autoPlay && ttsSupport && lesson) {
      setTimeout(() => startTTS(), 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPlay, ttsSupport, lesson]);

  const startTTS = () => {
    const text = buildSpeechText(lesson);
    const utt  = new SpeechSynthesisUtterance(text);
    utt.rate   = 0.95;
    utt.pitch  = 1;
    utt.onend  = () => setSpeaking(false);
    utt.onerror = () => setSpeaking(false);
    utterRef.current = utt;
    window.speechSynthesis.speak(utt);
    setSpeaking(true);
  };

  const toggleReadAloud = () => {
    if (speaking) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
      return;
    }
    startTTS();
  };

  // ── Bite Feed subscribe ─────────────────────────────────────────────────────
  const [bfEmail,       setBfEmail]       = useState(user?.email || '');
  const [bfSubscribed,  setBfSubscribed]  = useState(false);
  const [bfSubscribing, setBfSubscribing] = useState(false);
  const [bfError,       setBfError]       = useState('');

  useEffect(() => {
    if (user?.email && !bfEmail) setBfEmail(user.email);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBiteSubscribe = async () => {
    if (!bfEmail.trim()) { setBfError('Email is required'); return; }
    setBfSubscribing(true);
    setBfError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/api/bite-feed', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
        body:    JSON.stringify({
          topic:         subject,
          level:         searchParams?.level || 'beginner',
          ai_model:      searchParams?.aiModel || 'openai',
          notify_email:  bfEmail.trim(),
        }),
      });
      const json = await res.json();
      if (!res.ok) {
        // Surface a friendly message — never show raw DB errors to the user
        const raw = json.error || '';
        const friendly = raw.toLowerCase().includes('column') || raw.toLowerCase().includes('db')
          ? 'Could not save subscription — please try again in a moment.'
          : raw || 'Something went wrong. Please try again.';
        throw new Error(friendly);
      }
      if (json.alreadyExists) {
        setBfSubscribed(true); // already subscribed — treat as success
        return;
      }
      setBfSubscribed(true);
    } catch (err) {
      setBfError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setBfSubscribing(false);
    }
  };

  // ── Other models for retry strip ───────────────────────────────────────────
  const currentModel  = lesson.aiModel || 'openai';
  const otherModels   = Object.entries(MODEL_META).filter(([id]) => id !== currentModel);

  if (!lesson) return null;

  const modelMeta = MODEL_META[currentModel] || MODEL_META.openai;
  const saveItem  = buildTextSaveItem(lesson, subject || lesson.title);

  const handleShareClick = async () => {
    if (shareUrl) { setShowShare(true); return; }
    setCreatingLink(true);
    const url = await createShareableLesson(lesson, subject, user?.id);
    setShareUrl(url || window.location.origin);
    setCreatingLink(false);
    setShowShare(true);
  };

  return (
    <article className="text-content">

      {/* ── Hero (title + meta only) ── */}
      <div className="text-hero">
        <h1 className="text-title">{lesson.title}</h1>

        {/* Info badges */}
        <div className="text-meta">
          <span className="meta-tag time">⏱ {lesson.readingTime}</span>
          <span className="meta-tag">🍎 Bite-Lesson</span>
          <span className="meta-tag model-badge" style={{ color: modelMeta.color }}>
            🤖 {modelMeta.label}
          </span>
          {autoPlay && (
            <span className="meta-tag" style={{ color: '#0EA5E9' }}>🎧 Audio Mode</span>
          )}
        </div>
      </div>

      {/* ── Modals ── */}
      {showShare && (
        <ShareModal
          title={lesson.title}
          url={shareUrl}
          description={lesson.summary}
          content={{ content_type: 'text', title: lesson.title, subject, content_data: lesson }}
          onClose={() => setShowShare(false)}
        />
      )}
      {showCreateComm && (
        <CreateCommunityModal
          subject={subject}
          onCreated={() => setShowCreateComm(false)}
          onClose={() => setShowCreateComm(false)}
        />
      )}
      {showQuiz && (
        <QuizModal
          lesson={lesson}
          subject={subject}
          level={searchParams?.level || 'beginner'}
          onClose={() => setShowQuiz(false)}
        />
      )}
      {showTutor && (
        <TutorChat
          lesson={lesson}
          subject={subject}
          level={searchParams?.level || 'beginner'}
          onClose={() => setShowTutor(false)}
        />
      )}

      {/* ── Summary ── */}
      {lesson.summary && (
        <div className="text-summary">
          <p>{lesson.summary}</p>
        </div>
      )}

      {/* ── Key Points ── */}
      {lesson.keyPoints && lesson.keyPoints.length > 0 && (
        <div className="key-points">
          <h3>Key Points</h3>
          <ul className="key-points-list">
            {lesson.keyPoints.map((point, i) => (
              <li key={i}>
                <span className="kp-dot" />
                {point}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Body sections ── */}
      {lesson.content && lesson.content.length > 0 && (
        <div className="text-body">
          {lesson.content.map((section, i) => (
            <div key={i} className="text-section">
              <h3>{section.heading}</h3>
              <p>{section.body}</p>
            </div>
          ))}
        </div>
      )}

      {/* ── Practical example ── */}
      {lesson.practicalExample && (
        <div className="example-callout">
          <div className="example-callout-label">💡 Practical Example</div>
          <p>{lesson.practicalExample}</p>
        </div>
      )}

      {/* ── Takeaways ── */}
      {lesson.takeaways && lesson.takeaways.length > 0 && (
        <div className="takeaways">
          <h3>Key Takeaways</h3>
          <ul className="takeaways-list">
            {lesson.takeaways.map((t, i) => (
              <li key={i}>
                <span className="check-icon">✓</span>
                {t}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Next steps ── */}
      {lesson.nextSteps && (
        <div className="next-steps">
          <span className="next-steps-icon">🗺️</span>
          <div className="next-steps-content">
            <h4>Next Steps</h4>
            <p>{lesson.nextSteps}</p>
          </div>
        </div>
      )}

      {/* ── Try Another Model ── */}
      {(onRetryModel || onNextLesson) && (
        <div className="retry-model-strip">
          {onNextLesson && (
            <button className="keep-going-btn" onClick={onNextLesson} title="Generate a deeper follow-up lesson on the same topic">
              ▶▶ Keep Going
            </button>
          )}
          {onRetryModel && (
            <>
              <span className="retry-model-label">Try another model:</span>
              <div className="retry-model-btns">
                {otherModels.map(([id, meta]) => (
                  <button
                    key={id}
                    className="retry-model-btn"
                    onClick={() => onRetryModel(id)}
                    title={`Generate with ${meta.label}`}
                  >
                    {meta.emoji} {meta.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Action buttons (after all content) ── */}
      <div className="text-actions text-actions--bottom">
        <ReactionButtons subject={subject} userId={null} isGuest={false} />

        {ttsSupport && (
          <button
            className={`action-btn listen-btn ${speaking ? 'action-btn--active' : ''}`}
            onClick={toggleReadAloud}
          >
            {speaking ? '⏹ Stop' : '🔊 Listen'}
          </button>
        )}

        <button className="action-btn tutor-btn" onClick={() => setShowTutor(true)}>
          🤖 AI Tutor
        </button>

        <button className="quiz-btn-action" onClick={() => setShowQuiz(true)}>
          🧠 Quiz Me
        </button>

        {user && (
          <button className="action-btn community-btn" onClick={() => setShowCreateComm(true)}>
            🌐 Start Community
          </button>
        )}

        <button
          className="action-btn share-btn"
          onClick={handleShareClick}
          disabled={creatingLink}
        >
          {creatingLink ? '⏳ Creating link…' : '🔗 Share'}
        </button>

        <SaveButton item={saveItem} />
      </div>

      {/* ── Bite Feed subscribe ── */}
      {user && !bfSubscribed && (
        <div className="bite-feed-subscribe">
          <div className="bite-feed-subscribe__header">
            <span className="bite-feed-subscribe__icon">📡</span>
            <div>
              <strong>Bite Feed</strong>
              <p>Get a fresh follow-up lesson on <em>"{subject}"</em> delivered tomorrow.</p>
            </div>
          </div>
          <div className="bite-feed-subscribe__form">
            <input
              className="bite-feed-input"
              type="email"
              placeholder="Email for notification"
              value={bfEmail}
              onChange={e => setBfEmail(e.target.value)}
            />
            <button
              className="bite-feed-subscribe-btn"
              onClick={handleBiteSubscribe}
              disabled={bfSubscribing}
            >
              {bfSubscribing ? '…' : '📡 Subscribe'}
            </button>
          </div>
          {bfError && <p className="bite-feed-error">{bfError}</p>}
        </div>
      )}

      {user && bfSubscribed && (
        <div className="bite-feed-subscribed">
          ✅ Subscribed! Your next lesson on <strong>"{subject}"</strong> arrives tomorrow.
        </div>
      )}

    </article>
  );
}

export default TextContent;
